import React from 'react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const LAST_UPDATE_DATE = "05.03.2026";

  const styles = {
    container: {
      backgroundColor: '#000000',
      color: '#ffffff',
      minHeight: '100vh',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      lineHeight: '1.6',
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 20px',
    },
    h1: { fontSize: '2.5rem', marginBottom: '10px', fontWeight: '700' },
    lastUpdated: { color: '#888888', fontSize: '0.9rem', marginBottom: '30px' },
    h2: { fontSize: '1.8rem', marginTop: '40px', marginBottom: '20px', fontWeight: '600' },
    h3: { fontSize: '1.4rem', marginTop: '30px', marginBottom: '15px', fontWeight: '600' },
    section: { marginBottom: '30px' },
    paragraph: { marginBottom: '15px', fontSize: '1rem' },
    list: { marginLeft: '30px', marginBottom: '15px' },
    listItem: { marginBottom: '10px' },
    link: { color: '#4a9eff', textDecoration: 'none' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.h1}>{t('privacy.title')}</h1>
        <p style={styles.lastUpdated}>{t('privacy.last_updated', { date: LAST_UPDATE_DATE })}</p>

        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.intro.p1')}</p>
          <p style={styles.paragraph}>{t('privacy.intro.p2')}</p>
        </div>

        {/* Interpretation and Definitions */}
        <h2 style={styles.h2}>{t('privacy.interpretation.title')}</h2>

        <h3 style={styles.h3}>{t('privacy.interpretation.interpretation_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.interpretation.interpretation_text')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.interpretation.definitions_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.interpretation.definitions_intro')}</p>
          <ul style={styles.list}>
            {t('privacy.interpretation.definitions_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>

        {/* Collecting and Using */}
        <h2 style={styles.h2}>{t('privacy.collecting.title')}</h2>

        <h3 style={styles.h3}>{t('privacy.collecting.types_subtitle')}</h3>

        <h3 style={styles.h3}>{t('privacy.collecting.personal_data_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.personal_data_p1')}</p>
          <ul style={styles.list}>
            {t('privacy.collecting.personal_data_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.usage_data_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.usage_data_p1')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.usage_data_p2')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.usage_data_p3')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.usage_data_p4')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.advertising_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.advertising_p1')}</p>
          <p style={styles.paragraph}><strong>{t('privacy.collecting.advertising_collected_label')}</strong></p>
          <ul style={styles.list}>
            {t('privacy.collecting.advertising_collected_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.paragraph}>{t('privacy.collecting.advertising_p2')}</p>
          <p style={styles.paragraph}>
            {t('privacy.collecting.advertising_p3_before')}{' '}
            <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" style={styles.link}>
              https://policies.google.com/technologies/partner-sites
            </a>
          </p>
          <p style={styles.paragraph}><strong>{t('privacy.collecting.advertising_optout_label')}</strong></p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              {t('privacy.collecting.advertising_optout_list.0')}
            </li>
            <li style={styles.listItem}>
              {t('privacy.collecting.advertising_optout_list.1')}{' '}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={styles.link}>
                https://adssettings.google.com
              </a>
            </li>
          </ul>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.use_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.use_intro')}</p>
          <ul style={styles.list}>
            {t('privacy.collecting.use_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
          <p style={styles.paragraph}>{t('privacy.collecting.share_intro')}</p>
          <ul style={styles.list}>
            {t('privacy.collecting.share_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.retention_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.retention_p1')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.retention_p2')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.transfer_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.transfer_p1')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.transfer_p2')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.transfer_p3')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.collecting.delete_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.collecting.delete_p1')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.delete_p2')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.delete_p3')}</p>
          <p style={styles.paragraph}>{t('privacy.collecting.delete_p4')}</p>
        </div>

        {/* Disclosure */}
        <h2 style={styles.h2}>{t('privacy.disclosure.title')}</h2>

        <h3 style={styles.h3}>{t('privacy.disclosure.business_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.disclosure.business_p1')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.disclosure.law_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.disclosure.law_p1')}</p>
        </div>

        <h3 style={styles.h3}>{t('privacy.disclosure.legal_subtitle')}</h3>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.disclosure.legal_intro')}</p>
          <ul style={styles.list}>
            {t('privacy.disclosure.legal_list', { returnObjects: true }).map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Security */}
        <h2 style={styles.h2}>{t('privacy.security.title')}</h2>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.security.p1')}</p>
        </div>

        {/* Children's Privacy */}
        <h2 style={styles.h2}>{t('privacy.childrens_privacy.title')}</h2>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.childrens_privacy.p1')}</p>
          <p style={styles.paragraph}>{t('privacy.childrens_privacy.p2')}</p>
        </div>

        {/* Links */}
        <h2 style={styles.h2}>{t('privacy.links.title')}</h2>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.links.p1')}</p>
          <p style={styles.paragraph}>{t('privacy.links.p2')}</p>
        </div>

        {/* Changes */}
        <h2 style={styles.h2}>{t('privacy.changes.title')}</h2>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.changes.p1')}</p>
          <p style={styles.paragraph}>{t('privacy.changes.p2')}</p>
          <p style={styles.paragraph}>{t('privacy.changes.p3')}</p>
        </div>

        {/* Contact */}
        <h2 style={styles.h2}>{t('privacy.contact.title')}</h2>
        <div style={styles.section}>
          <p style={styles.paragraph}>{t('privacy.contact.p1')}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              {t('privacy.contact.email_label')}{' '}
              <a href="mailto:confesz@tech-center.com" style={styles.link}>
                confesz@tech-center.com
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;